import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import Button from "../components/uiMerge/newButton"
import njoyToastIcon from "../assets/svg/njoy-toast-icon.svg"
import { BrandName } from "../components/shared/BrandName";

const NOTIFICATION_KEY = 'njoy-offline-notification-date';
const INTERVAL = 14 * 24 * 60 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const OfflineNotification: React.FC = () => {
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Check if app is running as PWA
    const isInstalledPWA = window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    // Don't show notification if already running as PWA
    if (isInstalledPWA) {
      return;
    }

    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isInStandaloneMode = (window.navigator as any).standalone === true;

    // Check if notification should be shown
    const lastNotified = localStorage.getItem(NOTIFICATION_KEY);
    const now = Date.now();
    const shouldShowNotification = !lastNotified || now - parseInt(lastNotified, 10) >= INTERVAL;

    if (isIOS && !isInStandaloneMode && shouldShowNotification) {
      // Show iOS-specific instructions
      toast.info(
        <div className='flex flex-col gap-2 w-full'>
          <div className='flex items-center gap-2'>
            <img src={njoyToastIcon} alt="" />
            <p className='font-semibold'>Install <BrandName /> App!</p>
          </div>
          <p className='text-sm text-gray-300'>
            Tap <span className='inline-flex items-center mx-1'>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z" />
              </svg>
            </span> then "Add to Home Screen"
          </p>
        </div>,
        {
          position: 'top-right',
          autoClose: false,
          className: 'custom-toast',
          icon: false,
          closeButton: true,
          theme: "dark",
        }
      );
      localStorage.setItem(NOTIFICATION_KEY, now.toString());
      return;
    }

    // For non-iOS browsers
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Delay showing notification to ensure prompt is captured
    const timeoutId = setTimeout(() => {
      if (shouldShowNotification && deferredPromptRef.current) {
        toast.info(
          <div className='flex items-center justify-between w-full'>

            <div className='flex items-center gap-2'>
              <img src={njoyToastIcon} alt="" />
              <p >Install <BrandName /> App!</p>
            </div>

            <Button
              className='mr-3 !text-[11px] w-[89px]'
              size='sm'
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();

                if (deferredPromptRef.current) {
                  try {
                    await deferredPromptRef.current.prompt();
                    const choiceResult = await deferredPromptRef.current.userChoice;

                    if (choiceResult.outcome === 'accepted') {

                      toast.dismiss();
                    }

                    deferredPromptRef.current = null;
                  } catch (error) {
                    console.error('Error showing install prompt:', error);
                  }
                }
              }}
            >
              Install
            </Button>
          </div>,
          {
            position: 'top-right',
            autoClose: false,
            className: 'custom-toast',
            icon: false,
            closeButton: true,
            theme: "dark"
          }
        );
        localStorage.setItem(NOTIFICATION_KEY, now.toString());
      }
    }, 1500);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(timeoutId);
    };
  }, []);

  return null;
};